<template>
  <div class="recipient-form">
    <p v-if="!isEditing" class="heading bold">
      {{ $t('W2P_RECIPIENTS_NEW_RECIPIENT') }}
    </p>
    <validation-observer v-slot="{ invalid }">
      <form @submit.prevent>
        <pn-input-container
          :label="$t('NAME')"
          :invalid="errors.has('name')"
          :noicon="errors.has('name')"
          :valid="name.valid"
        >
          <input
            name="name"
            v-validate="'required|max:35'"
            v-model="recipient.name"
            autofocus
          />
          <div v-show="errors.has('name')" class="validation-text">
            {{ $t(errors.first('name')) }}
          </div>
        </pn-input-container>

        <pn-input-container
          :label="$t('ADDRESS')"
          :invalid="errors.has('address')"
          :noicon="errors.has('address')"
          :valid="address.valid"
        >
          <input
            name="address"
            v-validate="'required|max:35'"
            v-model="recipient.address"
          />
          <div v-show="errors.has('address')" class="validation-text">
            {{ $t(errors.first('address')) }}
          </div>
        </pn-input-container>

        <pn-input-container
          :label="$t('W2P_ADDRESS_OPTIONAL')"
          :invalid="errors.has('addressOptional')"
          :noicon="errors.has('addressOptional')"
          :valid="addressOptional.valid && recipient.addressOptional.length > 1"
        >
          <input
            name="addressOptional"
            v-validate="'max:35'"
            v-model="recipient.addressOptional"
          />
          <div v-show="errors.has('addressOptional')" class="validation-text">
            {{ $t(errors.first('addressOptional')) }}
          </div>
        </pn-input-container>

        <country-selection
          :searchable="true"
          :value="recipient.country"
          v-model="recipient.country"
        ></country-selection>

        <pn-input-container
          :label="$t('W2P_LETTER_POSTALCODE')"
          :invalid="errors.has('postalCode')"
          :noicon="errors.has('postalCode')"
          :valid="postalCode.valid"
        >
          <input
            name="postalCode"
            v-validate="`required|postalCode:${recipient.country}`"
            v-model="recipient.postalCode"
            @blur="fetchCity()"
          />
          <div v-show="errors.has('postalCode')" class="validation-text">
            {{ $t(errors.first('postalCode')) }}
          </div>
        </pn-input-container>

        <pn-input-container
          :label="$t('W2P_LETTER_CITY')"
          :invalid="errors.has('city')"
          :noicon="errors.has('city')"
          :valid="city.valid"
          :loading="loadingCity"
        >
          <input
            name="city"
            v-validate="'required|alpha_spaces'"
            v-model="recipient.city"
          />
          <div v-show="errors.has('city')" class="validation-text">
            {{ $t(errors.first('city')) }}
          </div>
        </pn-input-container>

        <div class="payment-slip-wrapper" v-if="paymentSlip">
          <pn-segmented-control
            @segmentchange="recipient.giroType = $event.target.value"
            class="radio-buttons"
            :value="recipient.giroType"
          >
            <pn-segment label="PostGiro" :value="'pg'"></pn-segment>
            <pn-segment label="BankGiro" :value="'bg'"></pn-segment>
          </pn-segmented-control>

          <pn-input-container
            :label="$t('W2P_GIRO_NUMBER')"
            :invalid="errors.has('giroNumber')"
            :noicon="errors.has('giroNumber')"
            :valid="giroNumber.valid"
          >
            <input
              name="giroNumber"
              v-validate="'required|max:35'"
              v-model="recipient.giroNumber"
            />
            <div v-show="errors.has('giroNumber')" class="validation-text">
              {{ $t(errors.first('giroNumber')) }}
            </div>
          </pn-input-container>

          <pn-input-container
            :label="$t('RECEIVER')"
            :invalid="errors.has('receiver')"
            :noicon="errors.has('receiver')"
            :valid="receiver.valid"
          >
            <input
              name="receiver"
              v-validate="'required|max:35|alpha_spaces'"
              v-model="recipient.receiver"
            />
            <div v-show="errors.has('receiver')" class="validation-text">
              {{ $t(errors.first('receiver')) }}
            </div>
          </pn-input-container>

          <pn-input-container
            :label="$t('W2P_SUM_SEK')"
            :invalid="errors.has('sum')"
            :noicon="errors.has('sum')"
            :valid="sum.valid"
          >
            <input
              name="sum"
              v-validate="'required|numeric'"
              v-model="recipient.sum"
            />
            <div v-show="errors.has('sum')" class="validation-text">
              {{ $t(errors.first('sum')) }}
            </div>
          </pn-input-container>

          <pn-input-container
            :label="$t('W2P_OCR_OPTIONAL')"
            :invalid="errors.has('ocr')"
            :noicon="errors.has('ocr')"
            :valid="ocr.valid && recipient.ocr.length > 1"
          >
            <input name="ocr" v-validate="'numeric'" v-model="recipient.ocr" />
            <div v-show="errors.has('ocr')" class="validation-text">
              {{ $t(errors.first('ocr')) }}
            </div>
          </pn-input-container>
          <multi-textfield-component
            max="6"
            label="Text (optional)"
            v-model="recipient.text"
            :addLabel="$t('W2P_ADD_TEXT')"
            rules="max:30"
          ></multi-textfield-component>
        </div>
        <pn-button-container class="save-button" size="medium">
          <button
            :disabled="isFormInvalid || invalid"
            type="submit"
            @click="submit()"
          >
            {{ $t('SAVE') }}
          </button>
        </pn-button-container>
      </form>
    </validation-observer>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState, mapMutations } from 'vuex'
import { mapFields, ValidationObserver } from 'vee-validate'
import CountrySelection from '@/components/common/country/CountryComponent'
import MultiTextfieldComponent from '@/components/common/MultiTextfieldComponent'

export default {
  data: () => ({
    recipient: {
      type: '',
      name: '',
      address: '',
      addressOptional: '',
      postalCode: '',
      city: '',
      country: 'SE',
      giroType: 'pg',
      giroNumber: '',
      receiver: '',
      sum: '',
      ocr: '',
      text: [],
      isEditing: false
    },
    loadingCity: false
  }),
  components: {
    CountrySelection,
    ValidationObserver,
    MultiTextfieldComponent
  },
  mounted() {
    if (this.prePopulate) {
      this.recipient = Object.assign({}, this.prePopulate)
    }

    if (!this.prePopulate) {
      this.recipient.type = this.service
    }
  },
  computed: {
    ...mapState(['lang']),
    ...mapState('letter/upload', ['letterOptions']),
    ...mapFields([
      'name',
      'address',
      'addressOptional',
      'postalCode',
      'city',
      'countryCode',
      'giroNumber',
      'receiver',
      'sum',
      'ocr'
    ]),
    paymentSlip() {
      return this.letterOptions.includePaymentSlip
    },
    service() {
      return this.letterOptions.includePaymentSlip
        ? 'paymentSlips'
        : 'information'
    },
    isFormInvalid() {
      return (
        this.errors.count() > 0 ||
        !Object.keys(this.fields).every(field => {
          return this.fields[field] && this.fields[field].valid
        })
      )
    },
    isEditing() {
      return this.prePopulate
    }
  },
  props: {
    prePopulate: {
      required: false,
      type: Object
    },
    index: {
      required: false,
      type: Number
    }
  },
  methods: {
    ...mapMutations('letter/recipient', ['ADD_RECIPIENT', 'EDIT_RECIPIENT']),
    submit() {
      this.recipient.text = this.recipient.text.filter(item => {
        return item.value !== ''
      })

      if (this.isEditing) {
        this.EDIT_RECIPIENT({
          recipient: this.recipient,
          index: this.index
        })
      } else {
        this.ADD_RECIPIENT(this.recipient)
      }

      this.$emit('submitted')
    },
    fetchCity() {
      if (!this.postalCode.valid) {
        return
      }
      this.loadingCity = true

      axios({
        method: 'get',
        url: `${process.env.VUE_APP_SUBSCRIPTIONS_API_BASE}/search/city/${this.recipient.country}/${this.recipient.postalCode}`
      })
        .then(response => {
          this.recipient.city = response.data.city
          this.loadingCity = false
        })
        .catch(e => {
          this.loadingCity = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.recipient-form {
  &.fade-enter-active {
    transition-delay: 0.8s;
  }
}

pn-input-container {
  width: 100%;
  &:not(:first-child) {
    margin-top: 1em;
  }
}

pn-select {
  width: 100%;
  margin: 1em 0 0 0;
}

.heading {
  margin-top: 2em;
  margin-bottom: 1em;
  color: $black;
}

.checkbox-container {
  border: 1px solid $gray400;
  padding: 1em;
  margin-top: 1em;
}

.save-button {
  width: 100%;
  margin-top: 1em;
}

.payment-slip-wrapper {
  margin-top: 1em;

  .radio-buttons {
    margin-top: 1em;
  }
}
</style>
